import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Copy, Share2, Twitter, Facebook, Linkedin, Mail, AlertCircle, Home } from 'lucide-react';
import { waitlistAPI } from '../services/waitlistApi';
import Footer from './Footer';

interface ConfirmedEmailPageProps {
  onTermsClick: () => void;
  onPrivacyClick: () => void;
  token?: string;
}

const ConfirmedEmailPage: React.FC<ConfirmedEmailPageProps> = ({ onTermsClick, onPrivacyClick, token }) => {
  const [verificationStatus, setVerificationStatus] = useState<'verifying' | 'success' | 'failed'>('verifying');
  const [verificationMessage, setVerificationMessage] = useState<string>('');
  const [referralCode, setReferralCode] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [shareMethod, setShareMethod] = useState<string>('');

  useEffect(() => {
    if (token) {
      verifyEmailConfirmation(token);
    } else {
      // No token provided
      setVerificationStatus('failed');
      setVerificationMessage('Invalid or no token provided. Please check your email for the correct confirmation link.');
    }
  }, [token]);

  const verifyEmailConfirmation = async (token: string) => {
    try {
      // Make API call to verify the confirmation token using the waitlistAPI service
      const response = await waitlistAPI.verifyEmail(token);

      if (response.success && response.data) {
        setVerificationStatus('success');
        setVerificationMessage('Your email has been successfully verified!');
        
        // Use the referral code from the server response
        if (response.data.referralCode) {
          setReferralCode(response.data.referralCode);
        }
      } else {
        // Server verification failed
        setVerificationStatus('failed');
        setVerificationMessage(response.message || 'Invalid or expired confirmation link. Please check your email for a valid confirmation link.');
      }
    } catch (error) {
      // Network error or other failure
      console.error('Email verification failed:', error);
      setVerificationStatus('failed');
      setVerificationMessage('Unable to verify your email. Please check your email for a valid confirmation link or try again later.');
    }
  };

  const generateReferralCode = () => {
    // Generate a random 8-character referral code
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setReferralCode(result);
  };

  const handleCopyReferralCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy referral code:', error);
    }
  };

  const handleCopyLink = async () => {
    try {
      const referralUrl = `${window.location.origin}?ref=${referralCode}`;
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setShareMethod('copy');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleSocialShare = (platform: string) => {
    setShareMethod(platform);
    const referralUrl = encodeURIComponent(`${window.location.origin}?ref=${referralCode}`);
    const text = encodeURIComponent('I just joined the Quellect waitlist! Join me and get early access to the future of content discovery.');
    
    let shareUrl = '';
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${referralUrl}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${referralUrl}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${referralUrl}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=Join Quellect Waitlist&body=${text}%0A%0A${referralUrl}`;
        break;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  // Show loading state while verifying
  if (verificationStatus === 'verifying') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 flex flex-col p-4 relative" style={{position: 'relative', zIndex: 9999}}>
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, type: "spring" }}
            className="max-w-2xl w-full text-center"
          >
            <div className="card p-12">
              <div className="w-24 h-24 bg-primary-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Verifying Your Email</h1>
              <p className="text-gray-600">Please wait while we confirm your email address...</p>
            </div>
          </motion.div>
        </div>
        
        {/* Footer */}
        <Footer onTermsClick={onTermsClick} onPrivacyClick={onPrivacyClick} />
      </div>
    );
  }

  // Show 404 page when verification fails
  if (verificationStatus === 'failed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 flex flex-col p-4 relative" style={{position: 'relative', zIndex: 9999}}>
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, type: "spring" }}
            className="max-w-2xl w-full text-center"
          >
            <div className="card p-12">
              <div className="w-24 h-24 bg-red-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                <AlertCircle className="w-12 h-12 text-red-600" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">404 - Page Not Found</h1>
              <p className="text-xl text-gray-600 mb-8">{verificationMessage}</p>
              
              <div className="space-y-4">
                <button
                  onClick={() => window.location.href = '/'}
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                >
                  <Home className="w-5 h-5" />
                  <span>Go to Homepage</span>
                </button>
                
                <p className="text-sm text-gray-500">
                  If you believe this is an error, please contact support or check your email for a valid confirmation link.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Footer */}
        <Footer onTermsClick={onTermsClick} onPrivacyClick={onPrivacyClick} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 flex items-center justify-center p-4 relative" style={{position: 'relative', zIndex: 9999}}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, type: "spring" }}
        className="max-w-4xl w-full"
      >
        {/* Success Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2, type: "spring" }}
            className="w-24 h-24 bg-green-100 rounded-full mx-auto mb-6 flex items-center justify-center"
          >
            <CheckCircle className="w-12 h-12 text-green-600" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
          >
            Email Confirmed! 🎉
          </motion.h1>
          

        </div>

        {/* Share Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="card text-center mb-8"
        >
          <div className="mb-6">
            <Share2 className="w-12 h-12 text-primary-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Here's a gift for verifying your email!
            </h2>
            <p className="text-gray-600">
                Share with 2 friends to move up a spot!
            </p>
          </div>

          {/* Copy Referral Link */}
          <div className="flex items-center space-x-3 max-w-md mx-auto mb-6">
            <input
              type="text"
              value={`${window.location.origin}?ref=${referralCode}`}
              readOnly
              className="flex-1 form-input text-sm text-center"
            />
            <button
              onClick={handleCopyLink}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                copied && shareMethod === 'copy'
                  ? 'bg-green-500 text-white'
                  : 'bg-primary-600 hover:bg-primary-700 text-white'
              }`}
            >
              {copied && shareMethod === 'copy' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Share Buttons */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <button
              onClick={() => handleSocialShare('twitter')}
              className="flex items-center justify-center space-x-2 p-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
            >
              <Twitter className="w-5 h-5" />
              <span className="hidden sm:inline">Twitter</span>
            </button>
            
            <button
              onClick={() => handleSocialShare('facebook')}
              className="flex items-center justify-center space-x-2 p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Facebook className="w-5 h-5" />
              <span className="hidden sm:inline">Facebook</span>
            </button>
            
            <button
              onClick={() => handleSocialShare('linkedin')}
              className="flex items-center justify-center space-x-2 p-3 bg-blue-700 hover:bg-blue-800 text-white rounded-lg transition-colors"
            >
              <Linkedin className="w-5 h-5" />
              <span className="hidden sm:inline">LinkedIn</span>
            </button>
            
            <button
              onClick={() => handleSocialShare('email')}
              className="flex items-center justify-center space-x-2 p-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              <Mail className="w-5 h-5" />
              <span className="hidden sm:inline">Email</span>
            </button>
          </div>

          {copied && shareMethod === 'copy' && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-green-600 text-sm mt-2"
            >
              Referral link copied to clipboard!
            </motion.p>
          )}

          {/* Referral Policy Link */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
            className="text-center mt-6"
          >
            <a
              href="/referral-policy"
              className="text-primary-600 hover:text-primary-700 text-sm underline"
            >
              View Referral Code Policy
            </a>
          </motion.div>
        </motion.div>

        {/* Questions Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="card text-center mb-8"
        >
          <div className="mb-6 pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Want to move up even further?
            </h2>
            <p className="text-gray-600 mb-6">
              If you haven't already, answer a few quick questions to help us personalize your experience<br />and boost your position on the waitlist!
            </p>
            <a
              href="/questions"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-secondary-600 hover:bg-secondary-700 text-white rounded-lg font-medium transition-colors"
            >
              <span>Answer Questions</span>
            </a>
          </div>
        </motion.div>

        {/* Footer */}
        <Footer onTermsClick={onTermsClick} onPrivacyClick={onPrivacyClick} />
      </motion.div>
    </div>
  );
};

export default ConfirmedEmailPage;
